import type {
  DynamicFieldType,
  DynamicInputField,
  DynamicInputJsonSchema,
  ToolKind,
  ToolRecord,
} from '@/api/types';

export interface DraftField {
  id: string;
  name: string;
  type: DynamicFieldType | 'array';
  itemsType: DynamicFieldType;
  description: string;
  required: boolean;
  enumValues: string;
  minimum?: number;
  maximum?: number;
}

export interface WizardDraft {
  kind: ToolKind;
  integrationId: string;
  toolName: string;
  description: string;
  enabled: boolean;
  fields: DraftField[];
  index: string;
  maxHits: string;
  templateJson: string;
  table: string;
  location: string;
  templateSql: string;
  apiPath: string;
  apiMethod: 'GET' | 'POST';
  apiQueryTemplate: string;
  apiBodyTemplate: string;
  apiHeaders: string;
  aiInstructions: string;
  maxResponseBytes: string;
  testInputs: Record<string, string>;
}

const ID_RE = /^[a-z][a-z0-9_]*$/;

export function emptyDraft(kind: ToolKind = 'elasticsearch'): WizardDraft {
  return {
    kind,
    integrationId: '',
    toolName: '',
    description: '',
    enabled: true,
    fields: [],
    index: kind === 'elasticsearch' ? 'ugo-logs-*' : '',
    maxHits: '',
    templateJson: '{\n  "query": { "match_all": {} }\n}',
    table: '',
    location: '',
    templateSql: 'SELECT 1',
    apiPath: '',
    apiMethod: 'GET',
    apiQueryTemplate: '',
    apiBodyTemplate: '',
    apiHeaders: '',
    aiInstructions: '',
    maxResponseBytes: '8192',
    testInputs: {},
  };
}

export function draftFromRecord(record: ToolRecord): WizardDraft {
  const required = new Set(record.inputJsonSchema.required ?? []);
  const fields: DraftField[] = Object.entries(record.inputJsonSchema.properties).map(
    ([name, def], i) => fieldFromSchema(name, def, required.has(name), i),
  );

  const base = {
    kind: record.kind,
    integrationId: record.integrationId ?? '',
    toolName: record.toolName,
    description: record.description,
    enabled: record.enabled,
    fields,
    index: '',
    maxHits: '',
    templateJson: '',
    table: '',
    location: '',
    templateSql: '',
    apiPath: '',
    apiMethod: 'GET' as const,
    apiQueryTemplate: '',
    apiBodyTemplate: '',
    apiHeaders: '',
    aiInstructions: '',
    maxResponseBytes: '8192',
    testInputs: {},
  };

  if (record.kind === 'elasticsearch') {
    const target = record.target as { index: string };
    return {
      ...base,
      index: target.index,
      maxHits: record.maxHits != null ? String(record.maxHits) : '',
      templateJson: typeof record.template === 'object' && record.template !== null
        ? JSON.stringify(record.template, null, 2)
        : '',
    };
  }
  if (record.kind === 'bigquery') {
    const target = record.target as { table: string; location?: string };
    return {
      ...base,
      table: target.table,
      location: target.location ?? '',
      templateSql: typeof record.template === 'string' ? record.template : '',
    };
  }
  const target = record.target as { path: string; method: string };
  return {
    ...base,
    apiPath: target.path,
    apiMethod: target.method as WizardDraft['apiMethod'],
    apiQueryTemplate: record.queryTemplate ? JSON.stringify(record.queryTemplate, null, 2) : '',
    apiBodyTemplate: typeof record.template === 'object' && record.template !== null
      ? JSON.stringify(record.template, null, 2)
      : '',
    apiHeaders: record.headers ? JSON.stringify(record.headers, null, 2) : '',
    aiInstructions: record.aiInstructions ?? '',
    maxResponseBytes: String(record.maxResponseBytes ?? 8192),
  };
}

function fieldFromSchema(
  name: string,
  def: DynamicInputField,
  required: boolean,
  idx: number,
): DraftField {
  return {
    id: `f-${idx}-${name}`,
    name,
    type: def.type,
    itemsType: def.items?.type ?? 'string',
    description: def.description ?? '',
    required,
    enumValues: (def.enum ?? []).join(', '),
    ...(typeof def.minimum === 'number' ? { minimum: def.minimum } : {}),
    ...(typeof def.maximum === 'number' ? { maximum: def.maximum } : {}),
  };
}

export interface ValidationResult {
  ok: boolean;
  errors: Partial<Record<keyof WizardDraft | 'fields' | 'template' | 'target' | 'scopes', string>>;
}

export function validateMetadata(draft: WizardDraft): ValidationResult {
  const errors: ValidationResult['errors'] = {};
  if (!ID_RE.test(draft.toolName)) errors.toolName = 'Format : ^[a-z][a-z0-9_]*$';
  if (draft.description.trim().length < 10) errors.description = 'Description trop courte (10 caractères min).';
  if (!draft.integrationId) errors.integrationId = 'Integration required.';
  return { ok: Object.keys(errors).length === 0, errors };
}

export function validateFields(draft: WizardDraft): ValidationResult {
  const errors: ValidationResult['errors'] = {};
  const names = new Set<string>();
  for (const f of draft.fields) {
    if (!ID_RE.test(f.name)) {
      errors.fields = `Champ "${f.name}" : nom invalide.`;
      break;
    }
    if (names.has(f.name)) {
      errors.fields = `Champ "${f.name}" : nom en doublon.`;
      break;
    }
    names.add(f.name);
  }
  return { ok: Object.keys(errors).length === 0, errors };
}

export function validateTarget(draft: WizardDraft): ValidationResult {
  const errors: ValidationResult['errors'] = {};
  if (draft.kind === 'elasticsearch') {
    if (!draft.index.trim()) errors.target = 'Index requis.';
    try {
      JSON.parse(draft.templateJson);
    } catch (e) {
      errors.template = `JSON invalide : ${(e as Error).message}`;
    }
  } else if (draft.kind === 'bigquery') {
    if (!draft.table.trim()) errors.target = 'Table requise (dataset.table).';
    if (draft.templateSql.trim().length < 8) errors.template = 'SQL trop court.';
  } else if (draft.kind === 'api') {
    if (!draft.apiPath.trim()) errors.target = 'Endpoint path required.';
    if (draft.apiBodyTemplate.trim()) {
      try {
        JSON.parse(draft.apiBodyTemplate);
      } catch (e) {
        errors.template = `Body JSON invalide : ${(e as Error).message}`;
      }
    }
    if (draft.apiQueryTemplate.trim()) {
      try {
        JSON.parse(draft.apiQueryTemplate);
      } catch (e) {
        errors.template = `Query params JSON invalide : ${(e as Error).message}`;
      }
    }
  }
  return { ok: Object.keys(errors).length === 0, errors };
}

export function draftToRecord(draft: WizardDraft): ToolRecord {
  const required: string[] = [];
  const properties: Record<string, DynamicInputField> = {};
  for (const f of draft.fields) {
    if (f.required) required.push(f.name);
    const def: DynamicInputField = { type: f.type };
    if (f.description.trim()) def.description = f.description.trim();
    if (f.type === 'string') {
      const values = f.enumValues.split(',').map((s) => s.trim()).filter(Boolean);
      if (values.length > 0) def.enum = values;
    }
    if (f.type === 'integer' || f.type === 'number') {
      if (typeof f.minimum === 'number') def.minimum = f.minimum;
      if (typeof f.maximum === 'number') def.maximum = f.maximum;
    }
    if (f.type === 'array') {
      def.items = { type: f.itemsType };
    }
    properties[f.name] = def;
  }
  const inputJsonSchema: DynamicInputJsonSchema = {
    type: 'object',
    properties,
    ...(required.length > 0 ? { required } : {}),
  };

  const base = {
    toolName: draft.toolName,
    description: draft.description,
    enabled: draft.enabled,
    integrationId: draft.integrationId,
    inputJsonSchema,
  };

  if (draft.kind === 'elasticsearch') {
    const template = JSON.parse(draft.templateJson) as Record<string, unknown>;
    return {
      ...base,
      kind: 'elasticsearch',
      target: { index: draft.index.trim() },
      template,
      ...(draft.maxHits.trim() ? { maxHits: Number(draft.maxHits) } : {}),
    };
  }
  if (draft.kind === 'bigquery') {
    return {
      ...base,
      kind: 'bigquery',
      target: {
        table: draft.table.trim(),
        ...(draft.location.trim() ? { location: draft.location.trim() } : {}),
      },
      template: draft.templateSql,
    };
  }
  const record: ToolRecord = {
    ...base,
    kind: 'api',
    target: {
      path: draft.apiPath.trim(),
      method: draft.apiMethod,
    },
    template: draft.apiBodyTemplate.trim()
      ? (JSON.parse(draft.apiBodyTemplate) as Record<string, unknown>)
      : null,
    ...(draft.apiQueryTemplate.trim()
      ? { queryTemplate: JSON.parse(draft.apiQueryTemplate) as Record<string, string> }
      : {}),
    ...(draft.apiHeaders.trim()
      ? { headers: JSON.parse(draft.apiHeaders) as Record<string, string> }
      : {}),
    ...(draft.aiInstructions.trim() ? { aiInstructions: draft.aiInstructions.trim() } : {}),
    maxResponseBytes: Number(draft.maxResponseBytes) || 8192,
  };
  return record;
}

export function coerceTestInputs(
  draft: WizardDraft,
): { ok: true; inputs: Record<string, unknown> } | { ok: false; error: string } {
  const out: Record<string, unknown> = {};
  for (const f of draft.fields) {
    const raw = draft.testInputs[f.name];
    if (raw === undefined || raw === '') {
      if (f.required) return { ok: false, error: `Champ "${f.name}" requis.` };
      continue;
    }
    try {
      out[f.name] = coerceOne(f, raw);
    } catch (e) {
      return { ok: false, error: `${f.name} : ${(e as Error).message}` };
    }
  }
  return { ok: true, inputs: out };
}

function coerceOne(f: DraftField, raw: string): unknown {
  switch (f.type) {
    case 'string':
      return raw;
    case 'integer': {
      const n = Number(raw);
      if (!Number.isInteger(n)) throw new Error('attendu: entier');
      return n;
    }
    case 'number': {
      const n = Number(raw);
      if (Number.isNaN(n)) throw new Error('attendu: nombre');
      return n;
    }
    case 'boolean':
      return raw === 'true' || raw === '1';
    case 'array': {
      const parts = raw.split(',').map((s) => s.trim()).filter(Boolean);
      switch (f.itemsType) {
        case 'string':
          return parts;
        case 'integer':
          return parts.map((p) => {
            const n = Number(p);
            if (!Number.isInteger(n)) throw new Error('attendu: entier');
            return n;
          });
        case 'number':
          return parts.map((p) => Number(p));
        case 'boolean':
          return parts.map((p) => p === 'true' || p === '1');
      }
    }
  }
}
